import FormSkeleton from '@/components/common/FormSkeleton';
import SusAddress from '@/components/Sus/detail/SusAddress';
import SusContact from '@/components/Sus/detail/SusContact';
import SusDetailField from '@/components/Sus/detail/SusDetailField';
import SusDetailSection from '@/components/Sus/detail/SusDetailSection';
import { useGetSusByIdQuery } from '@/features/api/endpoints/suEndpoints';
import { susDpiBasculeLabelKeys } from '@/models/sus.type';
import { DATE_FORMAT } from '@/shared/utils/date-utils';
import { Divider, Loader, SimpleGrid, Stack, Text } from '@mantine/core';
import { useTranslate } from '@tolgee/react';
import dayjs from 'dayjs';
import { lazy, Suspense } from 'react';

// Leaflet n'est chargé que lorsqu'une carte doit être affichée.
const SusMap = lazy(() => import('@/components/Sus/map/SusMap'));

type Props = {
  payload: { susId: number };
};

const EMPTY_VALUE = '—';

const Field = SusDetailField;

/** Heure `timetz` (« 08:30:00+00 ») affichée en « 08:30 ». */
const formatTime = (value: string | null | undefined): string =>
  value ? value.slice(0, 5) : EMPTY_VALUE;

/** Consultation en lecture seule des informations affichées par le formulaire d'édition. */
const SusDetailDrawer = ({ payload }: Props) => {
  const { t } = useTranslate();
  const { data: sus, isLoading, isError } = useGetSusByIdQuery(payload.susId);

  if (isLoading) {
    return <FormSkeleton label={t('admin.sus.form.loading')} />;
  }

  if (isError || !sus) {
    return (
      <Text c="red" role="alert">
        {t('admin.sus.form.error.load')}
      </Text>
    );
  }

  const formatBoolean = (value: boolean | null | undefined): string =>
    value == null
      ? EMPTY_VALUE
      : t(value ? 'admin.sus.transmission.oui' : 'admin.sus.transmission.non');

  const metadonnees = sus.metadonnees ?? {};

  const contacts = [...(sus.contacts ?? [])].sort((a, b) =>
    (a.libelle ?? '').localeCompare(b.libelle ?? '', 'fr', { sensitivity: 'base' }),
  );

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" component="dl" m={0}>
        <Field label={t('admin.sus.columns.finess_geo')}>{sus.finess_geo}</Field>
        <Field label={t('admin.sus.columns.numero_ordre')}>
          {sus.numero_ordre == null ? EMPTY_VALUE : t(`admin.sus.numero_ordre.${sus.numero_ordre}`)}
        </Field>
        <Field label={t('admin.sus.columns.date_gel')}>
          {sus.date_gel ? dayjs(sus.date_gel).format(DATE_FORMAT) : EMPTY_VALUE}
        </Field>
        <Field label={t('admin.sus.columns.nom')}>{sus.nom}</Field>
      </SimpleGrid>

      <Divider />
      <SusDetailSection title={t('admin.sus.detail.address')}>
        <SusAddress address={sus} />
      </SusDetailSection>
      <Divider />
      <SusDetailSection title={t('admin.sus.form.contacts.title')}>
        {contacts.length ? (
          <Stack gap={2}>
            {contacts.map(contact => (
              <SusContact key={contact.id ?? contact.email} contact={contact} />
            ))}
          </Stack>
        ) : (
          <Text>{EMPTY_VALUE}</Text>
        )}
      </SusDetailSection>
      <Divider />
      <SusDetailSection title={t('admin.sus.form.section.transmission')}>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" component="dl" m={0}>
          <Field label={t('admin.sus.detail.heure_premier_envoi_quotidien')}>
            {formatTime(sus.heure_premier_envoi_quotidien)}
          </Field>
          <Field label={t('admin.sus.detail.format_rpu')}>{sus.format_rpu ?? EMPTY_VALUE}</Field>
          <Field label={t('admin.sus.detail.calcul_automatique_indicateurs')}>
            {sus.calcul_automatique_indicateurs ?? EMPTY_VALUE}
          </Field>
          <Field label={t('admin.sus.detail.reactif_j_1')}>{formatBoolean(sus.reactif_j_1)}</Field>
          <Field label={t('admin.sus.detail.transmission_autorisee')}>
            {formatBoolean(sus.transmission_autorisee)}
          </Field>
        </SimpleGrid>
      </SusDetailSection>
      <Divider />
      <SusDetailSection title={t('admin.sus.form.section.seuils')}>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" component="dl" m={0}>
          <Field label={t('admin.sus.detail.seuil_bas_quotidien')}>
            {sus.seuil_bas_quotidien ?? EMPTY_VALUE}
          </Field>
          <Field label={t('admin.sus.detail.seuil_haut_quotidien')}>
            {sus.seuil_haut_quotidien ?? EMPTY_VALUE}
          </Field>
          <Field label={t('admin.sus.detail.seuil_bas_duree_passage')}>
            {sus.seuil_bas_duree_passage ?? EMPTY_VALUE}
          </Field>
          <Field label={t('admin.sus.detail.seuil_haut_duree_passage')}>
            {sus.seuil_haut_duree_passage ?? EMPTY_VALUE}
          </Field>
        </SimpleGrid>
      </SusDetailSection>
      <Divider />
      <SusDetailSection title={t('admin.sus.form.section.dpi')}>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" component="dl" m={0}>
          <Field label={t('admin.sus.detail.metadonnees.logiciel')}>
            {metadonnees.logiciel || EMPTY_VALUE}
          </Field>
          <Field label={t('admin.sus.detail.metadonnees.dpi_version')}>
            {metadonnees.dpi_version || EMPTY_VALUE}
          </Field>
          <Field label={t('admin.sus.detail.metadonnees.dpi_bascule')}>
            {metadonnees.dpi_bascule
              ? t(susDpiBasculeLabelKeys[metadonnees.dpi_bascule])
              : EMPTY_VALUE}
          </Field>
          {metadonnees.dpi_bascule !== 'Non' && (
            <Field label={t('admin.sus.detail.metadonnees.dpi_bascule_precisions')}>
              {metadonnees.dpi_bascule_precisions || EMPTY_VALUE}
            </Field>
          )}
          <Field label={t('admin.sus.detail.metadonnees.transmission_rpu_clos')}>
            {formatBoolean(metadonnees.transmission_rpu_clos)}
          </Field>
          <Field label={t('admin.sus.detail.metadonnees.supra_etab')}>
            {formatBoolean(metadonnees.supra_etab)}
          </Field>
        </SimpleGrid>
      </SusDetailSection>
      <Divider />
      <SusDetailSection title={t('admin.sus.detail.coordinates')}>
        {sus.latitude == null || sus.longitude == null ? (
          <Text>{EMPTY_VALUE}</Text>
        ) : (
          <Stack gap="xs">
            <Text>
              {t('admin.sus.detail.coordinates.value', {
                latitude: sus.latitude,
                longitude: sus.longitude,
              })}
            </Text>
            <Suspense fallback={<Loader size="sm" aria-label={t('admin.sus.form.loading')} />}>
              <SusMap
                latitude={sus.latitude}
                longitude={sus.longitude}
                label={sus.nom ?? undefined}
                ariaLabel={t('admin.sus.detail.map.label')}
              />
            </Suspense>
          </Stack>
        )}
      </SusDetailSection>
    </Stack>
  );
};

export default SusDetailDrawer;
